import React, { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { ActionLink } from '../../components/ui/ActionLink';
import { TextLink } from '../../components/ui/TextLink';
import { Section } from '../../components/ui/Section';
import { CourseCard } from '../../components/ui/CourseCard';
import { ArticleCard } from '../../components/ui/ArticleCard';
import { ArticleBody } from '../../components/blog/ArticleBody';
import { ArticleToc } from '../../components/blog/ArticleToc';
import { ShareLinks } from '../../components/blog/ShareLinks';
import { ReadingProgress } from '../../components/blog/ReadingProgress';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { logos } from '../../data/images';
import { NotFound } from '../NotFound';
import { articles } from '../../data/blog';
import { courses } from '../../data/courses';
import { slugify } from '../../utils/text';
export function BlogArticle() {
  const {
    slug
  } = useParams();
  const article = articles.find(item => item.slug === slug);
  const bodyRef = useRef<HTMLElement>(null);
  usePageMeta(article?.metaTitle ?? 'Article not found | ES World', article?.metaDescription ?? 'This page does not exist.');
  if (!article) return <NotFound data-id="element-1002" data-mp="1qz0yuze8erek" />;
  const related = courses.filter(course => article.relatedCourses.includes(course.slug));
  const moreArticles = articles.filter(item => item.slug !== article.slug).slice(0, 3);
  const before = article.blocks.slice(0, article.midAfter);
  const after = article.blocks.slice(article.midAfter);
  const toc = article.blocks.flatMap(block => block.type === 'h2' ? [{
    id: slugify(block.text),
    label: block.text
  }] : []);
  return <>
      <ReadingProgress targetRef={bodyRef} data-id="element-1003" data-mp="kk04gy4uuha3" />

      <header className="w-full bg-white" data-id="element-1004" data-mp="kiozww3l55hx">
        <div className="mx-auto max-w-[880px] px-5 pb-10 pt-12 text-center md:px-8 md:pb-14 md:pt-20" data-id="element-1005" data-mp="fkx77zv49fco">
          <Link to="/blog" className="inline-flex items-center gap-2 type-small font-semibold text-body transition-colors duration-150 hover:text-ink" data-id="element-1006" data-mp="2fn6jxv9frvzb">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1007" data-mp="2pdrf18pwiidh" />
            ES Blog
          </Link>
          <p className="mt-8 type-label text-brand-press" data-id="element-1008" data-mp="2uddu7npai8ox">{article.topic}</p>
          <h1 className="mt-4 type-h1 text-brand" data-id="element-1009" data-mp="1wplscgfaejb0">{article.title}</h1>
          <p className="mx-auto mt-6 max-w-2xl type-card-title font-normal leading-[1.55] text-body" data-id="element-1010" data-mp="1wrgbbp7001cg">{article.excerpt}</p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-id="element-1011" data-mp="26nrtgvt9rlet">
            <div className="flex items-center gap-3 text-left" data-id="element-1012" data-mp="2ap503846rsms">
              <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-cream p-1.5" aria-hidden="true" data-id="element-1013" data-mp="2aqg2p4lwaucb">
                <img src={logos.color} alt="" className="h-auto w-full object-contain" data-id="element-1014" data-mp="3m40dyrv0dq2a" />
              </span>
              <div data-id="element-1015" data-mp="1ahgbar8mz9lv">
                <p className="type-small font-bold text-ink" data-id="element-1016" data-mp="1afvip75vu0qn">ES World Editorial Team</p>
                <p className="type-small text-muted" data-id="element-1017" data-mp="16f392ct3lttu">
                  <time data-id="element-1018" data-mp="16hpea5sinx8w">{article.date}</time> · {article.readTime}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-content px-5 md:px-8" data-id="element-1019" data-mp="wogzaz82bxj2">
          <figure data-id="element-1020" data-mp="rqfhehmdi496">
            <img src={article.image} alt="" className="aspect-[16/9] w-full rounded-[28px] object-cover md:aspect-[21/9] md:rounded-[40px]" data-id="element-1021" data-mp="rqyp75ib7c9h" />
          </figure>
        </div>
      </header>

      <article ref={bodyRef} id="article-body" className="w-full bg-white" data-id="element-1022" data-mp="iph8pm0zp8k9">
        <div className="mx-auto grid max-w-content gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-12 lg:gap-12" data-id="element-1023" data-mp="irlniz0ffrzb">
          <aside className="hidden lg:col-span-3 lg:block" data-id="element-1024" data-mp="2d3s1u6omaovu">
            <div className="sticky top-[112px]" data-id="element-1025" data-mp="2d30f7ld001hp">
              <ArticleToc items={toc} data-id="element-1026" data-mp="2845mnrp8ayke" />
            </div>
          </aside>

          <div className="min-w-0 lg:col-span-6" data-id="element-1027" data-mp="282ufdhwkr8oj">
            <ArticleBody blocks={before} isStart data-id="element-1028" data-mp="14v2vx5qryms7" />

            <aside className="my-14 rounded-[28px] bg-cream p-7 md:p-9" aria-label="Recommended next step" data-id="element-1029" data-mp="19svzdr0p12os">
              <p className="type-h4 text-ink" data-id="element-1030" data-mp="vtd43yszdltw">Put this guide into practice</p>
              <p className="mt-3 type-body text-body" data-id="element-1031" data-mp="vvfs6dakp4vz">Our team can suggest a course that matches your goal, your level and your timetable.</p>
              <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center" data-id="element-1032" data-mp="m31mxakadg52">
                <ActionLink to={article.ctas.midPrimary.to} data-id="element-1033" data-mp="qu9x48tfx5ja">{article.ctas.midPrimary.label}</ActionLink>
                <TextLink to={article.ctas.midLink.to} data-id="element-1034" data-mp="d3eomxmusqg8">{article.ctas.midLink.label}</TextLink>
              </div>
            </aside>

            <ArticleBody blocks={after} data-id="element-1035" data-mp="h4gongolh6j9" />

            <footer className="mt-14 flex flex-col gap-5 border-t border-line pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-1036" data-mp="34pghwkrekd6">
              <p className="type-small font-bold text-ink" data-id="element-1037" data-mp="827e4qtbtd5v">Found this useful? Share it.</p>
              <ShareLinks title={article.title} data-id="element-1038" data-mp="3ko62pcop91aq" />
            </footer>
          </div>

          <aside className="hidden lg:col-span-3 lg:block" data-id="element-1039" data-mp="3fotgmu5njy97">
            <div className="sticky top-[112px] space-y-8" data-id="element-1040" data-mp="3fqeasio21f6j">
              <div data-id="element-1041" data-mp="3um2co15whq79">
                <p className="type-label text-muted" data-id="element-1042" data-mp="3umlwjnf42onx">Share</p>
                <div className="mt-4" data-id="element-1043" data-mp="3ply2esvl5rph">
                  <ShareLinks title={article.title} data-id="element-1044" data-mp="3pkwjy3c8q2xq" />
                </div>
              </div>
              <div className="rounded-[22px] bg-mist p-6" data-id="element-1045" data-mp="7erxxxikua7o">
                <p className="type-card-title text-ink" data-id="element-1046" data-mp="2i9t6xn8ybvc">Talk to an advisor</p>
                <p className="mt-2 type-small text-body" data-id="element-1047" data-mp="h64n2hczqma0">Free advice on courses, dates and visas — usually the same day.</p>
                <a href="#enquire" className="group mt-5 inline-flex items-center gap-1.5 type-button font-bold text-brand-press transition-colors duration-150 hover:text-ink" data-id="element-1048" data-mp="c71kr4oeau5q">
                  Enquire now
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1049" data-mp="t8p7ds3i7li3" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </article>

      {related.length ? <Section id="related" bg="grey" title="Courses from this guide" description="These courses connect to the advice in this article. Open a course to see dates, locations and prices." data-id="element-1050" data-mp="135a9o5o52ii8">
          <div className="grid gap-5 md:grid-cols-3" data-id="element-1051" data-mp="1byndw3rnl6jm">
            {related.map(course => <CourseCard key={course.slug} course={course} data-id="element-1052" data-mp="1gwyls8ye16z7" />)}
          </div>
        </Section> : null}

      {moreArticles.length ? <Section id="more" title="Keep reading" aside={<TextLink to="/blog" data-id="element-1054" data-mp="1qvxm94901wb4">All articles</TextLink>} data-id="element-1053" data-mp="1qw5yqv17mzls">
          <div className="grid gap-8 md:grid-cols-3" data-id="element-1055" data-mp="3nozj9o48g3rl">
            {moreArticles.map(item => <ArticleCard key={item.slug} article={item} data-id="element-1056" data-mp="3snj6rsyh5nm2" />)}
          </div>
        </Section> : null}

      <EnquirySection title="Plan your next step" description="Tell us what you want to learn and when. Our admissions team replies with a course plan and a price." submitLabel={article.ctas.bottomPrimary} defaultCourse={article.relatedCourses[0]} data-id="element-1057" data-mp="29tq10m7bc3wd" />
    </>;
}